/*
  assets/card-slider.js

  Product card image gallery. Native scroll-snap, no jQuery, no slick.

  Event delegation: every listener is bound once to document and finds its
  gallery through closest(). Nothing is bound to an individual card, so it
  does not matter how the theme replaces a card after a colour swatch click
  (whole card, only .card_slider, only the track), after filtering, load more
  or a section reload. State is read from the DOM each time and never cached
  on an element that may be thrown away.

  State on .card_slider:
    --dn-gal-n   number of slides, counted here (Liquid value is only a first paint default)
    --dn-gal-i   current index, written here

  Manual hook for swap code, optional:
    window.cardSliderRefresh(scopeElement)
*/
(function () {
  // The snippet emits one <script src> per card. Duplicates are no-ops.
  if (window.__cardGalleryReady) return;
  window.__cardGalleryReady = true;

  var ROOT = '[data-card-slider]';
  var TRACK = '[data-card-slider-track]';
  var SLIDE = '[data-card-slider-slide]';
  var PREV = '[data-card-slider-prev]';
  var NEXT = '[data-card-slider-next]';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function rootOf(node) {
    return node && node.closest ? node.closest(ROOT) : null;
  }

  function slidesOf(root) {
    var track = root.querySelector(TRACK);
    return track ? Array.prototype.slice.call(track.querySelectorAll(SLIDE)) : [];
  }

  // Measured, not assumed: card width changes across breakpoints and in the editor.
  function stepOf(root) {
    var first = root.querySelector(SLIDE);
    return first ? first.getBoundingClientRect().width : 0;
  }

  function clamp(value, max) {
    return Math.max(0, Math.min(value, max));
  }

  // Slides after the first hold their address in data-src behind a 1x1 gif.
  // Swapped in on first hover, tap, focus or scroll. Checked on every call
  // because a swap can bring in new deferred images under the same root.
  function loadDeferred(root) {
    var pending = root.querySelectorAll('img[data-src]');
    for (var i = 0; i < pending.length; i++) {
      var img = pending[i];
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
    }
  }

  function render(root, index, count) {
    root.style.setProperty('--dn-gal-n', Math.max(count, 1));
    root.style.setProperty('--dn-gal-i', index);
    root.setAttribute('data-card-slider-index', index);

    if (count < 2) root.setAttribute('data-card-slider-single', 'true');
    else root.removeAttribute('data-card-slider-single');

    // Arrows clamp at the ends rather than wrapping.
    var prev = root.querySelector(PREV);
    var next = root.querySelector(NEXT);
    if (prev) prev.setAttribute('data-dn-gal-off', index === 0 ? 'true' : 'false');
    if (next) next.setAttribute('data-dn-gal-off', index >= count - 1 ? 'true' : 'false');
  }

  // Scroll position is the source of truth. Returns the current index.
  function sync(root) {
    var track = root.querySelector(TRACK);
    var count = slidesOf(root).length;
    var width = stepOf(root);
    var index = 0;
    if (track && width) index = clamp(Math.round(track.scrollLeft / width), count - 1);
    render(root, index, count);
    return index;
  }

  function goTo(root, target) {
    var track = root.querySelector(TRACK);
    var count = slidesOf(root).length;
    if (!track || count < 2) return;

    loadDeferred(root);
    target = clamp(target, count - 1);
    track.scrollTo({
      left: Math.round(target * stepOf(root)),
      behavior: reduceMotion.matches ? 'auto' : 'smooth'
    });
    render(root, target, count);
  }

  // Sync the gallery that contains node, and any gallery inside node.
  function syncWithin(node) {
    if (!node || node.nodeType !== 1) return;
    var owner = rootOf(node);
    if (owner) sync(owner);
    if (node.querySelectorAll) {
      node.querySelectorAll(ROOT).forEach(function (root) {
        sync(root);
      });
    }
  }

  /* ---------- delegated listeners, bound once ---------- */

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest(PREV + ',' + NEXT);
    if (!btn) return;
    var root = rootOf(btn);
    if (!root) return;
    e.preventDefault();
    goTo(root, sync(root) + (btn.matches(NEXT) ? 1 : -1));
  });

  // scroll does not bubble, so capture it. A swipe moves the track without
  // any handler of ours running, the bar follows the scroll position.
  document.addEventListener(
    'scroll',
    function (e) {
      var track = e.target;
      if (!track || !track.matches || !track.matches(TRACK) || track.__dnTick) return;
      var root = rootOf(track);
      if (!root) return;
      loadDeferred(root);
      track.__dnTick = true;
      window.requestAnimationFrame(function () {
        track.__dnTick = false;
        sync(root);
      });
    },
    { capture: true, passive: true }
  );

  ['pointerover', 'touchstart', 'focusin'].forEach(function (type) {
    document.addEventListener(
      type,
      function (e) {
        var root = rootOf(e.target);
        if (root) loadDeferred(root);
      },
      { capture: true, passive: true }
    );
  });

  // Scroll position is in pixels, a width change would park the track
  // between two slides.
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      document.querySelectorAll(ROOT).forEach(function (root) {
        var track = root.querySelector(TRACK);
        var index = parseInt(root.getAttribute('data-card-slider-index'), 10) || 0;
        if (track) track.scrollTo({ left: Math.round(index * stepOf(root)), behavior: 'auto' });
      });
    }, 150);
  });

  /* ---------- state refresh when the DOM changes ---------- */

  function syncAll() {
    document.querySelectorAll(ROOT).forEach(function (root) {
      sync(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAll);
  } else {
    syncAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    syncWithin(e.target);
  });

  // Colour swatch swap, filtering, load more: new or replaced nodes. Also
  // covers a swap that only rewrites the inside of .card_slider, where the
  // root stays but slide count and track change.
  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) syncWithin(added[j]);
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  window.cardSliderRefresh = function (scope) {
    if (scope) syncWithin(scope);
    else syncAll();
  };
})();
